import { useState } from "react";

import { Button, TextField, View } from "@aws-amplify/ui-react";
import { COLORS, Heading } from "@stacklet/ui";

import {
  getLocalIdpUser,
  setLocalIdpCookies,
  setLocalIdpUser,
} from "app/utils/auth";
import { getConfigs } from "app/utils/configs";
import { dispatchRedirect } from "app/utils/redirection";
import StackletLogo from "assets/images/logo_vertical_color_1.svg?react";

import "./Login.css";

/**
 * Local-dev sign-in (platform's `just local-console`, VITE_DISABLE_AUTH=true).
 *
 * There's no Cognito locally, so this stands in for the Amplify Authenticator:
 * pick any seeded user and the local IdP issues a real JWT for them (there's no
 * password). The choice is stored so getAuthToken can mint that user's token
 * for GraphQL and cube. Navigate to `?logout` to switch users.
 */
interface Props {
  children: React.ReactNode;
}

export function LocalIdpLogin({ children }: Props) {
  const [user, setUser] = useState<string | null>(() => getLocalIdpUser());
  const [value, setValue] = useState("test-user");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) {
    return children;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const name = value.trim();
    if (!name) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Confirm the IdP can mint for this user (i.e. it's seeded) before
      // committing, so a typo surfaces here instead of as opaque 401s later.
      // Token minting goes via the vite proxy (/api/local-token) so remote
      // browsers work while the IdP keeps /token loopback-only.
      const { api } = await getConfigs();
      const tokenUrl = api.local_idp_token_url;
      if (!tokenUrl) {
        setError(
          "Local IdP token URL missing from config — is this local-dev?",
        );
        return;
      }
      const res = await fetch(
        `${tokenUrl}?username=${encodeURIComponent(name)}`,
      );
      if (!res.ok) {
        setError(
          res.status === 404
            ? `No active user "${name}" — seed one with \`just run-db-seed --username ${name} --role <role>\`.`
            : `Local IdP error (${res.status}).`,
        );
        return;
      }
      const { access_token: token } = (await res.json()) as {
        access_token: string;
      };
      // Set cookies eagerly so they're present before any navigation away from
      // the console (e.g. dispatchRedirect to an external service after login).
      setLocalIdpCookies(token);
      setLocalIdpUser(name);
    } catch {
      setError("Local IdP unreachable — is `just local-dev` running?");
      return;
    } finally {
      setBusy(false);
    }

    // Login already succeeded at this point, so a redirect failure here
    // shouldn't be reported as an IdP error — just enter the console.
    try {
      const params = new URLSearchParams(window.location.search);
      const redirected = await dispatchRedirect(
        params.get("redirect") ?? "",
        params.get("redirectPath") ?? "",
      );
      if (!redirected) setUser(name);
    } catch {
      setUser(name);
    }
  }

  return (
    <div className="grid grid-cols-[1.5fr_2fr]">
      <View padding="xxxl" textAlign="center">
        <div className="mb-2xl flex flex-col items-center justify-center gap-xs">
          <StackletLogo height="64" width="64" />
          <Heading className="font-normal" level={2}>
            Local dev sign-in
          </Heading>
        </div>
        <form className="flex flex-col items-center gap-lg" onSubmit={onSubmit}>
          <TextField
            descriptiveText="Any seeded user; the local IdP issues a JWT for them (no password)."
            errorMessage={error ?? undefined}
            hasError={error !== null}
            label="Username"
            onChange={(event) => setValue(event.target.value)}
            value={value}
            autoFocus
          />
          <Button
            isLoading={busy}
            style={{
              backgroundColor: COLORS.orange.base,
              color: "white",
              minWidth: "10rem",
            }}
            type="submit"
          >
            Sign in
          </Button>
        </form>
      </View>
      <div className="bg-legacy-navy-base" />
    </div>
  );
}
